import { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import { useLenis } from "./hooks/useLenis";
import Home from "./pages/Home";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { SiteGraphJsonLd } from "./components/SiteGraphJsonLd";
import { PasswordRecoveryModal } from "./components/PasswordRecoveryModal";
import { ForcePasswordChangeModal } from "./components/ForcePasswordChangeModal";
import { VIMSHOTTARI_DASHAS } from "./lib/nakshatras";

// Route-level code splitting: everything except Home (which must render
// fast for LCP/first paint) is lazy-loaded. Before this, every route —
// including Admin (pulls in recharts + react-simple-maps) and the
// dashboard — was eagerly bundled into the single main JS chunk that every
// visitor downloads and parses before seeing anything, even on the public
// marketing Home page. See docs/seo-trust-audit.md's Phase 1 Web Vitals
// item and the Lighthouse run referenced there (~540KB main bundle,
// long main-thread tasks on Home caused by parsing code no Home visitor
// ever executes).
const SeoHub = lazy(() => import("./pages/SeoHub"));
const SubToolPage = lazy(() =>
  import("./pages/SubToolPage").then((m) => ({ default: m.SubToolPage }))
);
const NakshatraDasha = lazy(() => import("./pages/NakshatraDasha"));
const Info = lazy(() => import("./pages/Info"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const Angebote = lazy(() => import("./pages/Angebote"));
const Beratung = lazy(() => import("./pages/Beratung"));
const Admin = lazy(() => import("./pages/Admin"));
const Login = lazy(() => import("./pages/Login"));
const Kundenbereich = lazy(() => import("./pages/Kundenbereich"));
const SavedCharts = lazy(() => import("./pages/dashboard/SavedCharts"));
const NakshatraGlossary = lazy(() =>
  import("./components/sections/NakshatraGlossary").then((m) => ({ default: m.NakshatraGlossary }))
);
const BirthChartForm = lazy(() =>
  import("./components/sections/BirthChartForm").then((m) => ({ default: m.BirthChartForm }))
);
const KnowledgePage = lazy(() => import("./pages/pSEO/KnowledgePage"));

/** Minimal, layout-neutral loading state for lazy route chunks — intentionally not a full skeleton (routes below Home aren't LCP-critical, so a brief blank moment during chunk fetch is an acceptable trade for the much smaller initial bundle). */
function RouteFallback() {
  return <div className="min-h-screen" aria-hidden="true" />;
}

export default function App() {
  useLenis(); // smooth scroll + GSAP ScrollTrigger sync (root-level, once)

  return (
    <>
    {/* Site-wide combined @graph structured data (Organization / Person /
        ProfessionalService / SoftwareApplication / Article). Mounted at the
        app root so it's present on every route — see
        components/SiteGraphJsonLd.tsx for why the whole graph stays on all
        pages rather than being split per route. */}
    <SiteGraphJsonLd />
    {/* Mounted at the app root (not /login) so the "set new password" flow
        surfaces on top of whatever route the reset-email redirect lands on
        — see components/PasswordRecoveryModal.tsx for why. */}
    <PasswordRecoveryModal />
    {/* Mounted at the app root, same rationale as PasswordRecoveryModal:
        must_change_password can be true right after login regardless of
        which route the user lands on (see lib/auth.tsx /
        components/ForcePasswordChangeModal.tsx). */}
    <ForcePasswordChangeModal />
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route
        path="/vedischer-rechner"
        element={
          <SeoHub
            title="Der Vedische Rechner"
            intro="Berechne dein vollständiges siderisches Geburtshoroskop — Lagna, Rashi, Navamsa und deine aktuelle Vimshottari Dasha — mit mathematischer Präzision."
            description="Kostenloser vedischer Horoskop-Rechner: berechne dein sidereales Geburtschart mit Lagna, Rashi, Nakshatra, Navamsa (D9) und Vimshottari-Dasha."
            path="/vedischer-rechner"
            hideCta
            faqPage="rechner"
          >
            <BirthChartForm />
          </SeoHub>
        }
      />
      <Route
        path="/nakshatra-bedeutung"
        element={
          <SeoHub
            title="Nakshatra Bedeutung"
            intro="Die 27 Mondhäuser der vedischen Astrologie. Entdecke die tiefe Bedeutung deines Geburts-Nakshatras und was es über deine Seele verrät."
            description="Alle 27 Nakshatras (Mondhäuser) der vedischen Astrologie mit Bedeutung, Symbol und herrschendem Planeten — finde dein Geburts-Nakshatra."
            path="/nakshatra-bedeutung"
          >
            <NakshatraGlossary />
          </SeoHub>
        }
      />
      <Route
        path="/lebensphasen-dasha"
        element={
          <SeoHub
            title="Lebensphasen & Dasha"
            intro="Die Vimshottari Dasha teilt dein Leben in planetare Zyklen. Verstehe, in welcher Phase du dich befindest und warum die Dinge gerade jetzt geschehen."
            description="Das Vimshottari-Dasha-System erklärt: 120-jähriger Zyklus planetarer Lebensphasen, der zeigt, wann Karriere, Liebe und Wohlstand reifen."
            path="/lebensphasen-dasha"
          >
            <div className="grid gap-4 sm:grid-cols-3">
              {VIMSHOTTARI_DASHAS.map((d) => (
                <div
                  key={d.planet}
                  className="glass p-6 text-center"
                >
                  <p className="font-display text-2xl text-gold-deep">{d.years}</p>
                  <p className="text-xs uppercase tracking-widest text-charcoal/50">Jahre</p>
                  <p className="mt-2 font-semibold text-charcoal">{d.planet}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 max-w-2xl text-sm text-charcoal/60">
              Der vollständige Vimshottari-Zyklus umfasst 120 Jahre, verteilt
              auf neun Planetenperioden (Mahadashas). Welche Dasha bei deiner
              Geburt aktiv war, bestimmt die exakte Position deines Mondes
              im Geburts-Nakshatra.
            </p>
          </SeoHub>
        }
      />
      {/* Vedic zodiac signs (Rashis) hub — carries the mandated FAQ + FAQPage
          JSON-LD for the "sternzeichen" topic (copy in i18n/seoContent.ts). */}
      <Route
        path="/vedische-sternzeichen"
        element={
          <SeoHub
            title="Die 12 Vedischen Sternzeichen (Rashis)"
            intro="Die vedische Astrologie kennt zwölf Rashis — die siderischen Tierkreiszeichen, ausgerichtet an den echten Fixsternen statt an den Jahreszeiten. Anders als in der westlichen Astrologie bestimmt hier klassisch der Mond dein Zeichen."
            description="Umfassender Leitfaden zu den 12 vedischen Sternzeichen (Rashis), ihren Unterschieden zum tropischen System und der Berechnung von Rashi & Aszendent."
            path="/vedische-sternzeichen"
            faqPage="sternzeichen"
          />
        }
      />
      {/* Topical-authority sub-tool landing pages (SEO/GEO). All three reuse
          the existing calculator via SubToolPage; content differs per search
          intent. See src/pages/SubToolPage.tsx. */}
      <Route
        path="/siderischer-aszendent-rechner"
        element={<SubToolPage subtool="aszendent" path="/siderischer-aszendent-rechner" />}
      />
      <Route
        path="/nakshatra-rechner"
        element={<SubToolPage subtool="nakshatra" path="/nakshatra-rechner" />}
      />
      <Route
        path="/dasha-rechner"
        element={<SubToolPage subtool="dasha" path="/dasha-rechner" />}
      />
      <Route path="/nakshatras-und-dasha" element={<NakshatraDasha />} />
      <Route path="/info" element={<Info />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/agb" element={<AGB />} />
      <Route path="/angebote" element={<Angebote />} />
      <Route path="/beratung" element={<Beratung />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/kundenbereich"
        element={
          <ProtectedRoute allow={["KUNDE"]}>
            <Kundenbereich />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kundenbereich/charts"
        element={
          <ProtectedRoute allow={["KUNDE"]}>
            <SavedCharts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute allow={["ADMIN", "MITARBEITER"]}>
            <Admin />
          </ProtectedRoute>
        }
      />
      {/* pSEO knowledge hub — isolated, no nav link, AI/SEO only */}
      <Route path="/wissen/:slug" element={<KnowledgePage />} />
    </Routes>
    </Suspense>
    </>
  );
}
